/**
 * The sticker that goes on a tool.
 *
 * A QR code or a barcode, plus the code in readable text underneath — always both.
 * A label whose code cannot be read by eye is useless the moment the print scuffs or
 * the scanner will not focus, and somebody in a van reading it out over the phone is a
 * real thing that happens.
 *
 * ## What the QR encodes
 *
 * A URL, not the bare code. Pointing any phone camera at it opens the item in the
 * portal; a bare string opens nothing and only works from inside our own scanner.
 * `GET /inventory/scan/:code` unpicks either, so nothing downstream cares which.
 *
 * ## Why a barcode option exists
 *
 * Some kit arrives with a manufacturer barcode already on it. Insisting on our own QR
 * would mean relabelling everything on day one, which is how a system gets abandoned in
 * week two. `codeKind` is per item for that reason.
 */

import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';

interface Props {
  code: string;
  kind: 'qr' | 'barcode';
  name?: string;
  /** Rendered px for the QR. The barcode sizes itself from its own options. */
  size?: number;
}

/**
 * What a scan should open.
 *
 * Built from the portal's own origin so a deployed instance encodes its real domain
 * and a local one encodes localhost — a hard-coded URL would print stickers pointing at
 * the wrong environment, which is not a mistake you can fix with a redeploy once the
 * labels are on the tools.
 */
export function scanUrl(code: string): string {
  return `${window.location.origin}/inventory/scan/${encodeURIComponent(code)}`;
}

export function InventoryLabel({ code, kind, name, size = 128 }: Props) {
  const barcodeRef = useRef<SVGSVGElement | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (kind !== 'qr') return;

    let cancelled = false;
    QRCode.toDataURL(scanUrl(code), {
      width: size * 2, // 2× so it stays sharp when printed
      margin: 1,
      // Medium correction: a label on a tool gets scratched, and the extra redundancy
      // costs a little density rather than a rescan in a dark plant room.
      errorCorrectionLevel: 'M',
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        // A label that fails to render still shows its code in text below, which is
        // enough to find the item by hand.
        if (!cancelled) setQrDataUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [code, kind, size]);

  useEffect(() => {
    if (kind !== 'barcode' || !barcodeRef.current) return;

    try {
      JsBarcode(barcodeRef.current, code, {
        // CODE128 handles our letters, digits and hyphens in one symbology and is what
        // every handheld scanner reads without configuration.
        format: 'CODE128',
        displayValue: false,
        margin: 0,
        height: 48,
        width: 1.6,
      });
    } catch {
      /* Same reasoning as the QR: the text below is the fallback. */
    }
  }, [code, kind]);

  return (
    <div className="inline-flex flex-col items-center gap-1.5">
      {kind === 'qr' ? (
        qrDataUrl ? (
          <img
            src={qrDataUrl}
            width={size}
            height={size}
            alt=""
            // Decorative: the code is in the text below, which a screen reader reads.
            aria-hidden
          />
        ) : (
          <div
            style={{ width: size, height: size }}
            className="rounded border border-border bg-surface-alt"
          />
        )
      ) : (
        <svg ref={barcodeRef} aria-hidden />
      )}

      <span className="text-xs font-bold tabular tracking-wide">{code}</span>
      {name ? (
        <span className="text-[10px] text-muted text-center max-w-[160px] leading-tight">
          {name}
        </span>
      ) : null}
    </div>
  );
}
